import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  NumberInput,
  NumberInputField,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { FiPlus, FiTrash2 } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import type { JsonObject, JsonValue } from './runtimeConfigJsonEditorUtils'

type JsonNodeType = 'string' | 'number' | 'boolean' | 'null' | 'object' | 'array'

type JsonValueEditorProps = {
  value: JsonValue
  onChange: (value: JsonValue) => void
  isDisabled?: boolean
  readonlyPaths?: readonly string[]
  path?: string
}

const getNodeType = (value: JsonValue): JsonNodeType => {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'array'
  if (typeof value === 'object') return 'object'
  return typeof value as 'string' | 'number' | 'boolean'
}

const createDefaultValue = (type: JsonNodeType): JsonValue => {
  switch (type) {
    case 'number': return 0
    case 'boolean': return false
    case 'null': return null
    case 'object': return {}
    case 'array': return []
    default: return ''
  }
}

const isReadonlyPath = (path: string, readonlyPaths: readonly string[]): boolean => (
  readonlyPaths.some(item => item === path)
)

function PrimitiveEditor({
  value,
  onChange,
  isDisabled,
}: Pick<JsonValueEditorProps, 'value' | 'onChange' | 'isDisabled'>) {
  const { t } = useTranslation()
  const typeOptions = [
    { value: 'string', label: t('systemConfig.runtimeConfig.jsonEditor.types.string', { defaultValue: 'String' }) },
    { value: 'number', label: t('systemConfig.runtimeConfig.jsonEditor.types.number', { defaultValue: 'Number' }) },
    { value: 'boolean', label: t('systemConfig.runtimeConfig.jsonEditor.types.boolean', { defaultValue: 'Boolean' }) },
    { value: 'null', label: t('systemConfig.runtimeConfig.jsonEditor.types.null', { defaultValue: 'Null' }) },
    { value: 'object', label: t('systemConfig.runtimeConfig.jsonEditor.types.object', { defaultValue: 'Object' }) },
    { value: 'array', label: t('systemConfig.runtimeConfig.jsonEditor.types.array', { defaultValue: 'Array' }) },
  ]
  const type = getNodeType(value)
  const setType = (nextType: string) => {
    onChange(createDefaultValue(nextType as JsonNodeType))
  }

  return (
    <HStack align="center" spacing={2} w="full">
      <Box flex={{ base: '0 0 108px', md: '0 0 120px' }} minW={0}>
        <DropdownSelect
          value={type}
          options={typeOptions}
          onChange={setType}
          ariaLabel={t('systemConfig.runtimeConfig.jsonEditor.valueType', { defaultValue: 'JSON value type' })}
          size="xs"
        />
      </Box>
      {type === 'boolean' ? (
        <Switch
          isChecked={value === true}
          onChange={event => onChange(event.target.checked)}
          isDisabled={isDisabled}
          aria-label={t('systemConfig.runtimeConfig.jsonEditor.booleanValue', { defaultValue: 'JSON boolean value' })}
        />
      ) : type === 'number' ? (
        <NumberInput
          value={String(value)}
          onChange={(_, next) => {
            if (Number.isFinite(next)) onChange(next)
          }}
          isDisabled={isDisabled}
          size="sm"
          flex={1}
          minW={0}
        >
          <NumberInputField aria-label={t('systemConfig.runtimeConfig.jsonEditor.numberValue', { defaultValue: 'JSON number value' })} />
        </NumberInput>
      ) : type === 'null' ? (
        <Text color="gray.500" fontSize="sm">null</Text>
      ) : (
        <Input
          value={String(value ?? '')}
          onChange={event => onChange(event.target.value)}
          isDisabled={isDisabled}
          size="sm"
          flex={1}
          minW={0}
          aria-label={t('systemConfig.runtimeConfig.jsonEditor.stringValue', { defaultValue: 'JSON string value' })}
        />
      )}
    </HStack>
  )
}

function ObjectEditor({
  value,
  onChange,
  isDisabled,
  readonlyPaths,
  path,
}: JsonValueEditorProps & { value: JsonObject }) {
  const { t } = useTranslation()
  const [keyDrafts, setKeyDrafts] = useState<Record<string, string>>({})

  useEffect(() => {
    setKeyDrafts(Object.fromEntries(Object.keys(value).map(key => [key, key])))
  }, [value])

  const renameField = (oldKey: string) => {
    const nextKey = (keyDrafts[oldKey] ?? oldKey).trim()
    if (!nextKey || (nextKey !== oldKey && Object.prototype.hasOwnProperty.call(value, nextKey))) {
      setKeyDrafts(current => ({ ...current, [oldKey]: oldKey }))
      return
    }
    if (nextKey === oldKey) return
    const nextValue = Object.fromEntries(
      Object.entries(value).map(([key, child]) => [key === oldKey ? nextKey : key, child]),
    )
    onChange(nextValue)
  }

  const updateField = (key: string, nextValue: JsonValue) => {
    onChange({ ...(value as JsonObject), [key]: nextValue })
  }
  const addField = () => {
    let key = 'newField'
    let index = 1
    while (Object.prototype.hasOwnProperty.call(value, key)) {
      key = `newField${index++}`
    }
    onChange({ ...(value as JsonObject), [key]: '' })
  }

  return (
    <VStack
      align="stretch"
      spacing={1}
      pl={path ? 3 : 0}
      borderLeft={path ? '2px solid' : undefined}
      borderColor={path ? 'blackAlpha.100' : undefined}
    >
      {Object.entries(value).map(([key, child]) => {
        const childPath = path ? `${path}.${key}` : key
        const readOnly = isReadonlyPath(childPath, readonlyPaths ?? [])
        const childType = getNodeType(child)
        const isContainer = childType === 'object' || childType === 'array'
        return (
          <Box key={key} borderWidth="1px" borderRadius="md" p={2}>
            <HStack
              align="start"
              spacing={2}
              flexWrap={{ base: 'wrap', md: 'nowrap' }}
            >
              <Input
                value={keyDrafts[key] ?? key}
                size="sm"
                flex={{ base: '1 1 100%', md: isContainer ? '1 1 auto' : '0 0 160px' }}
                minW={0}
                isDisabled={isDisabled || readOnly}
                aria-label={t('systemConfig.runtimeConfig.jsonEditor.fieldName', { defaultValue: 'JSON field name' })}
                isInvalid={Boolean(
                  (keyDrafts[key] ?? key).trim() === ''
                    || ((keyDrafts[key] ?? key).trim() !== key
                      && Object.prototype.hasOwnProperty.call(value, (keyDrafts[key] ?? key).trim())),
                )}
                onChange={event => setKeyDrafts(current => ({ ...current, [key]: event.target.value }))}
                onBlur={() => renameField(key)}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    renameField(key)
                  }
                }}
              />
              {!isContainer && (
                <Box flex="1 1 auto" minW={0}>
                  <JsonValueEditor
                    value={child}
                    onChange={nextValue => updateField(key, nextValue)}
                    isDisabled={isDisabled || readOnly}
                    readonlyPaths={readonlyPaths}
                    path={childPath}
                  />
                </Box>
              )}
              <IconButton
                size="sm"
                variant="ghost"
                aria-label={t('systemConfig.runtimeConfig.jsonEditor.deleteField', { defaultValue: 'Delete JSON field' })}
                icon={<FiTrash2 />}
                isDisabled={isDisabled || readOnly}
                onClick={() => {
                  const next = { ...(value as JsonObject) }
                  delete next[key]
                  onChange(next)
                }}
              />
            </HStack>
            {isContainer && (
              <Box mt={2} minW={0}>
                <JsonValueEditor
                  value={child}
                  onChange={nextValue => updateField(key, nextValue)}
                  isDisabled={isDisabled || readOnly}
                  readonlyPaths={readonlyPaths}
                  path={childPath}
                />
              </Box>
            )}
          </Box>
        )
      })}
      <Button
        alignSelf="flex-start"
        size="xs"
        variant="outline"
        leftIcon={<FiPlus />}
        onClick={addField}
        isDisabled={isDisabled}
      >
        {t('systemConfig.runtimeConfig.jsonEditor.addField', { defaultValue: 'Add field' })}
      </Button>
    </VStack>
  )
}

function ArrayEditor({ value, onChange, isDisabled, readonlyPaths, path }: JsonValueEditorProps & { value: JsonValue[] }) {
  const { t } = useTranslation()
  return (
    <VStack align="stretch" spacing={1} pl={path ? 3 : 0}>
      {value.map((child, index) => (
        <HStack key={index} align="start" spacing={2} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
          <Text w="6" pt={2} fontSize="xs" color="gray.500">{index + 1}</Text>
          <Box flex="1 1 auto" minW={0}>
            <JsonValueEditor
              value={child}
              onChange={nextValue => {
                const next = [...value]
                next[index] = nextValue
                onChange(next)
              }}
              isDisabled={isDisabled}
              readonlyPaths={readonlyPaths}
              path={`${path ?? ''}[${index}]`}
            />
          </Box>
          <IconButton
            size="sm"
            variant="ghost"
            aria-label={t('systemConfig.runtimeConfig.jsonEditor.deleteItem', { defaultValue: 'Delete JSON array item' })}
            icon={<FiTrash2 />}
            isDisabled={isDisabled}
            onClick={() => onChange(value.filter((_, itemIndex) => itemIndex !== index))}
          />
        </HStack>
      ))}
      <Button
        alignSelf="flex-start"
        size="xs"
        variant="outline"
        leftIcon={<FiPlus />}
        onClick={() => onChange([...value, ''])}
        isDisabled={isDisabled}
      >
        {t('systemConfig.runtimeConfig.jsonEditor.addItem', { defaultValue: 'Add item' })}
      </Button>
    </VStack>
  )
}

export function JsonValueEditor(props: JsonValueEditorProps) {
  const type = getNodeType(props.value)
  if (type === 'object') {
    return <ObjectEditor {...props} value={props.value as JsonObject} />
  }
  if (type === 'array') {
    return <ArrayEditor {...props} value={props.value as JsonValue[]} />
  }
  return <PrimitiveEditor {...props} />
}
